type SuggestionSection<Item> = {
  label: string;
  entries: { item: Item; index: number }[];
};

/**
 * Groups items under their section heading. Sections named in `order` come
 * first, in that order; any other section follows in the order its first item
 * arrived. Items keep their relative order within a section, and each entry's
 * `index` is its position in display order, which the keyboard follows.
 */
export function groupIntoSections<Item>({
  items,
  getSection,
  order = [],
}: {
  items: readonly Item[];
  getSection: (item: Item) => string;
  order?: readonly string[];
}): SuggestionSection<Item>[] {
  const byLabel = new Map<string, Item[]>(order.map((label) => [label, []]));
  for (const item of items) {
    const label = getSection(item);
    const section = byLabel.get(label);
    if (section) {
      section.push(item);
    } else {
      byLabel.set(label, [item]);
    }
  }
  let index = 0;
  return [...byLabel]
    .filter(([, sectionItems]) => sectionItems.length > 0)
    .map(([label, sectionItems]) => ({
      label,
      entries: sectionItems.map((item) => ({ item, index: index++ })),
    }));
}

/** Whether what's typed, ignoring case and a leading `/`, appears in any of the fields. */
export function matchesQuery(
  fields: readonly string[],
  query: string,
): boolean {
  const needle = query.trim().toLowerCase().replace(/^\//, "");
  return (
    !needle || fields.some((field) => field.toLowerCase().includes(needle))
  );
}
